"use client"

import { FormEvent, useEffect, useMemo, useRef, useState } from "react"
import { useParams } from "next/navigation"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import SearchingWorm from "@modules/layout/components/chat-widget/searching-worm"
import type { ChatProductCard } from "@lib/chat/types"
import {
  getOrCreateVisitorKey,
  listSupportChatMessages,
  sendSupportChatMessage,
  startSupportChatSession,
  endSupportChatSession,
  fetchSupportChatCaptcha,
  type SupportChatMessage,
  type SupportChatProfile,
} from "@lib/data/support-chat"
import { t } from "@lib/i18n"
import { DEFAULT_LOCALE, toPickerLocale } from "@lib/i18n/locales"

function readLocaleCookie(): string {
  if (typeof document === "undefined") {
    return DEFAULT_LOCALE
  }
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith("_medusa_locale="))
  if (!match) {
    return DEFAULT_LOCALE
  }
  return toPickerLocale(decodeURIComponent(match.split("=")[1] || ""))
}

type UiMessage = {
  id: string
  role: "user" | "assistant"
  content: string
  products?: ChatProductCard[]
}

type TeamUiMessage = {
  id: string
  sender: "customer" | "admin" | "system"
  body: string
  author_name?: string | null
  created_at: string
}

type ChatMode = "assistant" | "team" | "line"

const STORAGE_KEY = "vx-chat-messages-v2"
const OPEN_KEY = "vx-chat-open-v1"
const MODE_KEY = "vx-chat-mode-v1"
const TEAM_CONV_KEY = "vx-support-conversation-v1"
const TEAM_PROFILE_KEY = "vx-support-profile-v1"

const SUGGESTED_PROMPTS = [
  "มี switch PoE 8 พอร์ตไหม",
  "เปรียบเทียบ MikroTik กับ Ubiquiti สำหรับ SMB",
  "แนะนำ UniFi access point รุ่นไหนดี",
]

function resolveLineOaUrl(): string | null {
  const override = (process.env.NEXT_PUBLIC_LINE_OA_URL || "").trim()
  if (override) {
    return override
  }
  const rawId = (process.env.NEXT_PUBLIC_LINE_OA_ID || "").trim()
  if (!rawId) {
    return null
  }
  const id = rawId.startsWith("@") ? rawId : `@${rawId}`
  return `https://line.me/R/ti/p/${id}`
}

function resolveLineOaLabel(): string | null {
  const rawId = (process.env.NEXT_PUBLIC_LINE_OA_ID || "").trim()
  if (rawId) {
    return rawId.startsWith("@") ? rawId : `@${rawId}`
  }
  const url = (process.env.NEXT_PUBLIC_LINE_OA_URL || "").trim()
  const match = url.match(/@[A-Za-z0-9._-]+/)
  return match ? match[0] : null
}

function loadMessages(): UiMessage[] {
  if (typeof window === "undefined") {
    return []
  }
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return []
    }
    const parsed = JSON.parse(raw) as UiMessage[]
    if (!Array.isArray(parsed)) {
      return []
    }
    return parsed.filter((m) => m.id !== "welcome")
  } catch {
    return []
  }
}

function loadOpenState(): boolean {
  if (typeof window === "undefined") {
    return false
  }
  try {
    return sessionStorage.getItem(OPEN_KEY) === "1"
  } catch {
    return false
  }
}

function loadMode(): ChatMode {
  if (typeof window === "undefined") {
    return "assistant"
  }
  try {
    const raw = sessionStorage.getItem(MODE_KEY)
    if (raw === "line" || raw === "team" || raw === "assistant") {
      return raw
    }
  } catch {
    /* ignore */
  }
  return "assistant"
}

function mapTeamMessages(rows: SupportChatMessage[]): TeamUiMessage[] {
  return rows.map((m) => ({
    id: m.id,
    sender:
      m.sender === "admin" || m.sender === "system" ? m.sender : "customer",
    body: m.body,
    author_name: m.author_name,
    created_at: m.created_at,
  }))
}

export default function ChatWidget() {
  const { countryCode } = useParams()
  const country = typeof countryCode === "string" ? countryCode : "th"
  const lineOaUrl = resolveLineOaUrl()
  const lineOaLabel = resolveLineOaLabel()
  const lineEnabled = Boolean(lineOaUrl)

  const [locale, setLocale] = useState(DEFAULT_LOCALE)
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<ChatMode>("assistant")
  const [input, setInput] = useState("")
  const [loading, setLoading] = useState(false)
  const [messages, setMessages] = useState<UiMessage[]>([])
  const [teamMessages, setTeamMessages] = useState<TeamUiMessage[]>([])
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [visitorKey, setVisitorKey] = useState("")
  const [teamProfile, setTeamProfile] = useState<SupportChatProfile | null>(
    null
  )
  const [teamName, setTeamName] = useState("")
  const [teamEmail, setTeamEmail] = useState("")
  const [captchaQuestion, setCaptchaQuestion] = useState("")
  const [captchaToken, setCaptchaToken] = useState("")
  const [captchaAnswer, setCaptchaAnswer] = useState("")
  const [hydrated, setHydrated] = useState(false)
  const [teamError, setTeamError] = useState<string | null>(null)
  const [teamNotice, setTeamNotice] = useState<string | null>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const teamReady = Boolean(teamProfile && conversationId)

  const hasUserMessages = messages.some((m) => m.role === "user")
  const showWelcome = mode === "assistant" && !hasUserMessages

  useEffect(() => {
    setLocale(readLocaleCookie())
  }, [open])

  useEffect(() => {
    setMessages(loadMessages())
    setOpen(loadOpenState())
    const nextMode = loadMode()
    setMode(nextMode === "line" && !lineEnabled ? "assistant" : nextMode)
    const key = getOrCreateVisitorKey()
    setVisitorKey(key)
    try {
      const savedConv = localStorage.getItem(TEAM_CONV_KEY)
      const rawProfile = localStorage.getItem(TEAM_PROFILE_KEY)
      let profile: SupportChatProfile | null = null
      if (rawProfile) {
        const parsed = JSON.parse(rawProfile) as SupportChatProfile
        if (parsed?.name && parsed?.email) {
          profile = parsed
          setTeamProfile(parsed)
          setTeamName(parsed.name)
          setTeamEmail(parsed.email)
        }
      }
      if (savedConv && profile) {
        setConversationId(savedConv)
      } else {
        setConversationId(null)
        localStorage.removeItem(TEAM_CONV_KEY)
        if (!profile) {
          localStorage.removeItem(TEAM_PROFILE_KEY)
        }
      }
    } catch {
      setConversationId(null)
    }
    setHydrated(true)
  }, [lineEnabled])

  const refreshCaptcha = async () => {
    try {
      const captcha = await fetchSupportChatCaptcha()
      setCaptchaQuestion(captcha.question)
      setCaptchaToken(captcha.token)
      setCaptchaAnswer("")
    } catch {
      setCaptchaQuestion("")
      setCaptchaToken("")
    }
  }

  useEffect(() => {
    if (!hydrated || mode !== "team" || teamReady) {
      return
    }
    void refreshCaptcha()
  }, [hydrated, mode, teamReady])

  useEffect(() => {
    if (!hydrated) {
      return
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages))
  }, [messages, hydrated])

  useEffect(() => {
    if (!hydrated) {
      return
    }
    sessionStorage.setItem(OPEN_KEY, open ? "1" : "0")
  }, [open, hydrated])

  useEffect(() => {
    if (!hydrated) {
      return
    }
    sessionStorage.setItem(MODE_KEY, mode)
  }, [mode, hydrated])

  useEffect(() => {
    if (!open || (mode !== "assistant" && mode !== "team")) {
      return
    }
    const el = listRef.current
    if (el) {
      el.scrollTop = el.scrollHeight
    }
  }, [messages, teamMessages, open, loading, mode])

  // Poll admin replies while team mode is open and session started
  useEffect(() => {
    if (!open || mode !== "team" || !visitorKey || !conversationId || !teamReady) {
      return
    }

    let cancelled = false

    const pull = async () => {
      try {
        const data = await listSupportChatMessages({
          visitor_key: visitorKey,
          conversation_id: conversationId,
        })
        if (!cancelled) {
          setTeamMessages(mapTeamMessages(data.messages))
        }
      } catch {
        /* keep last good state */
      }
    }

    void pull()
    const timer = window.setInterval(() => void pull(), 4000)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [open, mode, visitorKey, conversationId, teamReady])

  const openAssistant = () => {
    setMode("assistant")
    setOpen(true)
  }

  const openTeam = () => {
    setMode("team")
    setOpen(true)
  }

  const openLinePanel = () => {
    if (!lineEnabled) {
      return
    }
    setMode("line")
    setOpen(true)
  }

  const clearAssistantChat = () => {
    if (loading) {
      return
    }
    setMessages([])
    setInput("")
    try {
      sessionStorage.removeItem(STORAGE_KEY)
    } catch {
      /* ignore */
    }
  }

  const sendText = async (rawText: string) => {
    const text = rawText.trim()
    if (!text || loading || mode !== "assistant") {
      return
    }

    const userMsg: UiMessage = {
      id: `u-${Date.now()}`,
      role: "user",
      content: text,
    }

    const nextMessages = [...messages, userMsg]
    setMessages(nextMessages)
    setInput("")
    setLoading(true)

    try {
      const history = nextMessages.map((m) => ({
        role: m.role,
        content: m.content,
      }))

      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: history,
          countryCode: country,
          locale,
        }),
      })

      const data = (await res.json()) as {
        message?: string
        products?: ChatProductCard[]
        error?: string
      }

      const reply =
        data.message ||
        (res.status === 503
          ? t(locale, "chat.unavailable")
          : t(locale, "chat.errorGeneric"))

      setMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          role: "assistant",
          content: reply,
          products: data.products || [],
        },
      ])
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          role: "assistant",
          content: t(locale, "chat.connectFailed"),
        },
      ])
    } finally {
      setLoading(false)
    }
  }

  const startTeamSession = async (event?: FormEvent) => {
    event?.preventDefault()
    if (!visitorKey || loading) {
      return
    }
    setLoading(true)
    setTeamError(null)
    setTeamNotice(null)
    try {
      const result = await startSupportChatSession({
        visitor_key: visitorKey,
        name: teamName.trim(),
        email: teamEmail.trim(),
        captcha_token: captchaToken,
        captcha_answer: captchaAnswer.trim(),
      })
      const profile = { name: result.name, email: result.email }
      setTeamProfile(profile)
      setConversationId(result.conversation_id)
      try {
        localStorage.setItem(TEAM_CONV_KEY, result.conversation_id)
        localStorage.setItem(TEAM_PROFILE_KEY, JSON.stringify(profile))
      } catch {
        /* ignore */
      }
      setTeamMessages([])
    } catch (error) {
      setTeamError(
        error instanceof Error
          ? error.message
          : "เริ่มแชทไม่สำเร็จ กรุณาลองใหม่"
      )
      void refreshCaptcha()
    } finally {
      setLoading(false)
    }
  }

  const endTeamSession = async () => {
    if (!visitorKey || !conversationId || loading) {
      return
    }
    setLoading(true)
    setTeamError(null)
    try {
      const result = await endSupportChatSession({
        visitor_key: visitorKey,
        conversation_id: conversationId,
      })
      setTeamMessages([])
      setConversationId(null)
      setTeamProfile(null)
      try {
        localStorage.removeItem(TEAM_CONV_KEY)
        localStorage.removeItem(TEAM_PROFILE_KEY)
      } catch {
        /* ignore */
      }
      setTeamNotice(
        result.email_sent
          ? `จบการสนทนาแล้ว — ส่งสำเนาไปที่ ${result.emailed_cc || teamEmail} และ ${result.emailed_to || "sales@vxwork.com"}`
          : "จบการสนทนาแล้ว (ยังส่งอีเมลสำเนาไม่สำเร็จ — ทีมจะติดต่อทางอื่น)"
      )
      void refreshCaptcha()
    } catch (error) {
      setTeamError(
        error instanceof Error ? error.message : "จบแชทไม่สำเร็จ"
      )
    } finally {
      setLoading(false)
    }
  }

  const sendTeamText = async (rawText: string) => {
    const text = rawText.trim()
    if (!text || loading || !visitorKey || !teamReady || !teamProfile) {
      return
    }

    setInput("")
    setLoading(true)
    setTeamError(null)

    try {
      const result = await sendSupportChatMessage({
        visitor_key: visitorKey,
        conversation_id: conversationId,
        body: text,
        name: teamProfile.name,
        contact: teamProfile.email,
      })
      setConversationId(result.conversation_id)
      try {
        localStorage.setItem(TEAM_CONV_KEY, result.conversation_id)
      } catch {
        /* ignore */
      }
      setTeamMessages((prev) => {
        const withoutDup = prev.filter((m) => m.id !== result.message.id)
        return [
          ...withoutDup,
          {
            id: result.message.id,
            sender: "customer",
            body: result.message.body,
            author_name: result.message.author_name,
            created_at: result.message.created_at,
          },
        ]
      })
    } catch {
      setTeamError("ส่งข้อความไม่สำเร็จ กรุณาลองใหม่")
    } finally {
      setLoading(false)
    }
  }

  const sendMessage = async (event?: FormEvent) => {
    event?.preventDefault()
    if (mode === "team") {
      await sendTeamText(input)
      return
    }
    await sendText(input)
  }

  const title =
    mode === "line"
      ? t(locale, "chat.line")
      : mode === "team"
        ? t(locale, "chat.team")
        : "VX Assistant"

  const subtitle =
    mode === "line"
      ? t(locale, "chat.subtitleLine")
      : mode === "team"
        ? t(locale, "chat.subtitleTeam")
        : t(locale, "chat.subtitleAssistant")

  const suggestedPrompts = useMemo(() => {
    if (locale === "en-US") {
      return [
        "Need a PoE switch for a 40-room hotel",
        "Compare UniFi AP models for an office",
        "Looking for MikroTik CRS328",
      ]
    }
    if (locale === "my-MM") {
      return [
        "ဟိုတယ်အခန်း ၄၀ အတွက် PoE switch လိုပါတယ်",
        "ရုံးအတွက် UniFi AP မော်ဒယ်များ နှိုင်းယှဉ်ပေးပါ",
        "MikroTik CRS328 ရှာနေပါတယ်",
      ]
    }
    return SUGGESTED_PROMPTS
  }, [locale])

  return (
    <>
      {!open && (
        <div className="fixed right-0 top-1/2 z-[60] hidden -translate-y-1/2 flex-col gap-2 small:flex">
          <button
            type="button"
            onClick={openAssistant}
            className="flex flex-col items-center justify-center gap-2 rounded-l-xl border border-r-0 border-vx-line bg-vx-accent px-2.5 py-4 text-white shadow-[0_8px_24px_rgba(59,130,246,0.35)] transition-colors hover:bg-[#2563eb]"
            aria-label={t(locale, "chat.askProducts")}
            data-testid="chat-widget-toggle"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden
              className="shrink-0"
            >
              <path
                d="M5 6.5A2.5 2.5 0 0 1 7.5 4h9A2.5 2.5 0 0 1 19 6.5v7A2.5 2.5 0 0 1 16.5 16H10l-4.2 3.15A.75.75 0 0 1 4.5 18.5V6.5Z"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinejoin="round"
              />
            </svg>
            <span
              className="font-mono text-[12px] font-semibold uppercase tracking-[0.18em]"
              style={{ writingMode: "vertical-rl" }}
            >
              {t(locale, "chat.askProducts")}
            </span>
          </button>
          <button
            type="button"
            onClick={openTeam}
            className="flex flex-col items-center justify-center gap-2 rounded-l-xl border border-r-0 border-vx-line bg-vx-elevated px-2.5 py-4 text-vx-ink shadow-[0_8px_24px_rgba(0,0,0,0.25)] transition-colors hover:bg-vx-bg"
            aria-label={t(locale, "chat.team")}
            data-testid="chat-team-toggle"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden
            >
              <path
                d="M7 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm10 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM3.5 19.5c0-2.8 2.5-5 5.5-5h1c1.2 0 2.3.3 3.2.9M13 14.5c3 0 5.5 2.2 5.5 5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
            <span
              className="font-mono text-[12px] font-semibold uppercase tracking-[0.18em]"
              style={{ writingMode: "vertical-rl" }}
            >
              {t(locale, "chat.team")}
            </span>
          </button>
          {lineEnabled && (
            <button
              type="button"
              onClick={openLinePanel}
              className="flex flex-col items-center justify-center gap-2 rounded-l-xl border border-r-0 border-[#05a849] bg-[#06C755] px-2.5 py-4 text-white shadow-[0_8px_24px_rgba(6,199,85,0.35)] transition-opacity hover:opacity-90"
              aria-label={t(locale, "chat.line")}
              data-testid="chat-line-toggle"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden
                className="shrink-0"
              >
                <path d="M12 3C6.48 3 2 6.58 2 11c0 3.95 3.5 7.26 8.25 7.91L12 22l1.88-3.13C18.58 18.16 22 14.9 22 11c0-4.42-4.48-8-10-8zm-3.1 9.4H7.5V8.9h1.4v3.5zm3.05 0h-1.4V8.9h1.4v3.5zm3.05 0h-1.4V8.9H15v3.5z" />
              </svg>
              <span
                className="font-mono text-[12px] font-semibold uppercase tracking-[0.18em]"
                style={{ writingMode: "vertical-rl" }}
              >
                LINE
              </span>
            </button>
          )}
        </div>
      )}

      {!open && (
        <div className="chat-mobile-fab-stack fixed bottom-4 right-4 z-[60] flex flex-col items-end gap-2 small:hidden">
          {lineEnabled && (
            <a
              href={lineOaUrl || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-full bg-[#06C755] px-4 py-3 text-white shadow-[0_8px_24px_rgba(6,199,85,0.45)] transition-opacity hover:opacity-90"
              aria-label="เปิดแชท LINE"
              data-testid="chat-line-toggle-mobile"
            >
              <span className="font-mono text-[12px] font-semibold uppercase tracking-[0.12em]">
                LINE
              </span>
            </a>
          )}
          <button
            type="button"
            onClick={openTeam}
            className="flex items-center gap-2 rounded-full border border-vx-line bg-vx-elevated px-4 py-3 text-vx-ink shadow-[0_8px_24px_rgba(0,0,0,0.25)]"
            aria-label={t(locale, "chat.team")}
            data-testid="chat-team-toggle-mobile"
          >
            <span className="font-mono text-[12px] font-semibold uppercase tracking-[0.12em]">
              {t(locale, "chat.team")}
            </span>
          </button>
          <button
            type="button"
            onClick={openAssistant}
            className="flex items-center gap-2 rounded-full bg-vx-accent px-4 py-3 text-white shadow-[0_8px_24px_rgba(59,130,246,0.45)]"
            aria-label={t(locale, "chat.askProducts")}
            data-testid="chat-widget-toggle-mobile"
          >
            <span className="font-mono text-[12px] font-semibold uppercase tracking-[0.12em]">
              {t(locale, "chat.askProducts")}
            </span>
          </button>
        </div>
      )}

      {open && (
        <div
          className="fixed inset-3 z-[60] flex flex-col overflow-hidden rounded-2xl border border-vx-line bg-vx-surface shadow-[0_16px_48px_rgba(0,0,0,0.5)] small:inset-y-0 small:right-0 small:left-auto small:h-screen small:w-[min(440px,100vw)] small:rounded-none small:rounded-l-2xl small:border-r-0"
          data-testid="chat-widget-panel"
        >
          <div className="shrink-0 border-b border-vx-line bg-vx-elevated">
            <div className="flex items-center justify-between px-5 py-4">
              <div>
                <p className="font-mono text-[16px] font-semibold uppercase tracking-[0.12em] text-vx-ink">
                  {title}
                </p>
                <p className="mt-1 text-[14px] text-vx-muted">{subtitle}</p>
              </div>
              <div className="flex items-center gap-1">
                {mode === "assistant" && hasUserMessages && (
                  <button
                    type="button"
                    onClick={clearAssistantChat}
                    disabled={loading}
                    className="rounded-lg px-2 py-1 font-mono text-[13px] uppercase tracking-[0.12em] text-vx-muted hover:bg-vx-bg hover:text-vx-ink disabled:opacity-50"
                    aria-label={t(locale, "chat.clear")}
                    data-testid="chat-clear"
                  >
                    {t(locale, "chat.clear")}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded-lg px-2 py-1 font-mono text-[13px] uppercase tracking-[0.12em] text-vx-muted hover:bg-vx-bg hover:text-vx-ink"
                  aria-label={t(locale, "chat.close")}
                >
                  {t(locale, "chat.close")}
                </button>
              </div>
            </div>

            <div
              className="flex gap-1 px-4 pb-3"
              role="tablist"
              aria-label={t(locale, "chat.modeAssistant")}
            >
              <button
                type="button"
                role="tab"
                aria-selected={mode === "assistant"}
                onClick={() => setMode("assistant")}
                className={[
                  "flex-1 rounded-lg px-2 py-2 font-mono text-[12px] font-semibold uppercase tracking-[0.08em] transition-colors",
                  mode === "assistant"
                    ? "bg-vx-accent text-white"
                    : "bg-vx-bg text-vx-muted hover:text-vx-ink",
                ].join(" ")}
                data-testid="chat-mode-assistant"
              >
                {t(locale, "chat.modeAssistant")}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === "team"}
                onClick={() => setMode("team")}
                className={[
                  "flex-1 rounded-lg px-2 py-2 font-mono text-[12px] font-semibold uppercase tracking-[0.08em] transition-colors",
                  mode === "team"
                    ? "bg-vx-ink text-white"
                    : "bg-vx-bg text-vx-muted hover:text-vx-ink",
                ].join(" ")}
                data-testid="chat-mode-team"
              >
                {t(locale, "chat.modeTeam")}
              </button>
              {lineEnabled && (
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "line"}
                  onClick={() => setMode("line")}
                  className={[
                    "flex-1 rounded-lg px-2 py-2 font-mono text-[12px] font-semibold uppercase tracking-[0.08em] transition-colors",
                    mode === "line"
                      ? "bg-[#06C755] text-white"
                      : "bg-vx-bg text-vx-muted hover:text-vx-ink",
                  ].join(" ")}
                  data-testid="chat-mode-line"
                >
                  LINE
                </button>
              )}
            </div>
          </div>

          {mode === "line" && lineOaUrl ? (
            <div
              className="flex flex-1 flex-col justify-center gap-6 px-6 py-8"
              data-testid="chat-line-panel"
            >
              <div>
                <p className="text-[11px] font-mono uppercase tracking-[0.16em] text-[#06C755]">
                  {t(locale, "chat.lineTitle")}
                </p>
                <h2 className="mt-3 text-2xl font-semibold leading-snug tracking-tight text-vx-ink small:text-[28px]">
                  {t(locale, "chat.lineBody")}
                </h2>
                <p className="mt-3 max-w-sm text-sm leading-6 text-vx-muted">
                  {t(locale, "chat.lineHint")}
                </p>
              </div>
              <a
                href={lineOaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#06C755] px-5 py-3.5 font-mono text-[12px] font-semibold uppercase tracking-[0.12em] text-white transition-opacity hover:opacity-90"
                data-testid="chat-line-open"
              >
                {t(locale, "chat.lineCta")}
              </a>
              {lineOaLabel && (
                <p className="text-center text-[12px] text-vx-muted">
                  OA: {lineOaLabel}
                </p>
              )}
            </div>
          ) : mode === "team" ? (
            <>
              {!teamReady ? (
                <form
                  onSubmit={startTeamSession}
                  className="flex flex-1 flex-col justify-center gap-4 overflow-y-auto px-6 py-8"
                  data-testid="chat-team-preform"
                >
                  <div>
                    <p className="text-[11px] font-mono uppercase tracking-[0.16em] text-vx-ink">
                      ติดต่อทีม VX Work
                    </p>
                    <h2 className="mt-3 text-2xl font-semibold leading-snug tracking-tight text-vx-ink">
                      กรอกข้อมูลก่อนเริ่มแชท
                    </h2>
                    <p className="mt-3 max-w-sm text-sm leading-6 text-vx-muted">
                      หลังจบการคุย ระบบจะส่งสำเนาประวัติแชทไปที่อีเมลของคุณ
                      และ sales@vxwork.com
                    </p>
                  </div>

                  <label className="block text-sm text-vx-ink">
                    ชื่อ
                    <input
                      type="text"
                      required
                      minLength={2}
                      value={teamName}
                      onChange={(e) => setTeamName(e.target.value)}
                      className="mt-1.5 w-full rounded-xl border border-vx-line bg-vx-bg px-3 py-2.5 text-[15px] outline-none focus:border-vx-accent"
                      data-testid="chat-team-name"
                      autoComplete="name"
                    />
                  </label>

                  <label className="block text-sm text-vx-ink">
                    อีเมล
                    <input
                      type="email"
                      required
                      value={teamEmail}
                      onChange={(e) => setTeamEmail(e.target.value)}
                      className="mt-1.5 w-full rounded-xl border border-vx-line bg-vx-bg px-3 py-2.5 text-[15px] outline-none focus:border-vx-accent"
                      data-testid="chat-team-email"
                      autoComplete="email"
                    />
                  </label>

                  <label className="block text-sm text-vx-ink">
                    Captcha: {captchaQuestion || "…"} = ?
                    <input
                      type="text"
                      required
                      inputMode="numeric"
                      value={captchaAnswer}
                      onChange={(e) => setCaptchaAnswer(e.target.value)}
                      className="mt-1.5 w-full rounded-xl border border-vx-line bg-vx-bg px-3 py-2.5 text-[15px] outline-none focus:border-vx-accent"
                      data-testid="chat-team-captcha"
                      autoComplete="off"
                    />
                  </label>

                  {teamError && (
                    <p className="text-sm text-red-500" data-testid="chat-team-error">
                      {teamError}
                    </p>
                  )}
                  {teamNotice && (
                    <p className="text-sm text-vx-muted" data-testid="chat-team-notice">
                      {teamNotice}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={
                      loading ||
                      !teamName.trim() ||
                      !teamEmail.trim() ||
                      !captchaAnswer.trim() ||
                      !captchaToken
                    }
                    className="rounded-xl bg-vx-ink px-5 py-3.5 font-mono text-[12px] font-semibold uppercase tracking-[0.12em] text-white disabled:opacity-50"
                    data-testid="chat-team-start"
                  >
                    {loading ? "กำลังเริ่ม…" : "เริ่มแชท"}
                  </button>
                </form>
              ) : (
                <>
                  <div className="flex shrink-0 items-center justify-between border-b border-vx-line bg-vx-bg px-5 py-2.5">
                    <p className="truncate text-[14px] text-vx-muted">
                      {teamProfile?.name} · {teamProfile?.email}
                    </p>
                    <button
                      type="button"
                      onClick={() => void endTeamSession()}
                      disabled={loading}
                      className="shrink-0 rounded-lg px-2 py-1 font-mono text-[12px] uppercase tracking-[0.1em] text-vx-muted hover:bg-vx-elevated hover:text-vx-ink disabled:opacity-50"
                      data-testid="chat-team-end"
                    >
                      จบการคุย
                    </button>
                  </div>
                  <div
                    ref={listRef}
                    className="flex-1 space-y-4 overflow-y-auto px-5 py-5"
                    data-testid="chat-team-panel"
                  >
                    {!teamMessages.length && (
                      <div className="flex min-h-[40%] flex-col justify-center gap-3 py-4">
                        <p className="text-[11px] font-mono uppercase tracking-[0.16em] text-vx-ink">
                          ติดต่อทีม VX Work
                        </p>
                        <h2 className="text-2xl font-semibold leading-snug tracking-tight text-vx-ink">
                          {t(locale, "chat.teamHint")}
                        </h2>
                        <p className="max-w-sm text-sm leading-6 text-vx-muted">
                          ข้อความจะถึงทีมทันที เมื่อจบการคุย
                          ระบบจะส่งสำเนาประวัติไปอีเมลของคุณและ sales@vxwork.com
                        </p>
                      </div>
                    )}

                    {teamMessages.map((message) => (
                      <div
                        key={message.id}
                        className={[
                          "max-w-[95%] rounded-2xl px-4 py-3 text-[15px] leading-7",
                          message.sender === "customer"
                            ? "ml-auto bg-vx-ink text-white"
                            : "mr-auto border border-vx-line bg-vx-bg text-vx-ink",
                        ].join(" ")}
                      >
                        {message.sender === "admin" && message.author_name && (
                          <p className="mb-1 text-[11px] font-mono uppercase tracking-[0.12em] text-vx-muted">
                            {message.author_name}
                          </p>
                        )}
                        <p className="whitespace-pre-wrap">{message.body}</p>
                      </div>
                    ))}

                    {loading && (
                      <div className="mr-auto rounded-2xl border border-vx-line bg-vx-bg px-4 py-3 text-[15px] text-vx-muted">
                        กำลังส่ง…
                      </div>
                    )}
                    {teamError && (
                      <p
                        className="text-sm text-red-500"
                        data-testid="chat-team-error"
                      >
                        {teamError}
                      </p>
                    )}
                  </div>

                  <form
                    onSubmit={sendMessage}
                    className="shrink-0 border-t border-vx-line bg-vx-elevated p-4"
                  >
                    <div className="flex items-end gap-2 rounded-2xl border border-vx-line bg-vx-bg p-2 focus-within:border-vx-accent">
                      <label className="sr-only" htmlFor="chat-team-input">
                        ข้อความถึงทีม
                      </label>
                      <textarea
                        id="chat-team-input"
                        rows={2}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault()
                            void sendMessage()
                          }
                        }}
                        placeholder={t(locale, "chat.placeholderTeam")}
                        className="max-h-32 min-h-[52px] flex-1 resize-none border-0 bg-transparent px-2 py-2 text-[15px] text-vx-ink outline-none placeholder:text-vx-muted"
                        data-testid="chat-team-input"
                      />
                      <button
                        type="submit"
                        disabled={loading || !input.trim()}
                        className="mb-0.5 shrink-0 rounded-xl bg-vx-ink px-4 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-white disabled:opacity-50"
                        data-testid="chat-team-send"
                      >
                        ส่ง
                      </button>
                    </div>
                  </form>
                </>
              )}
            </>
          ) : (
            <>
              <div
                ref={listRef}
                className="flex-1 space-y-4 overflow-y-auto px-5 py-5"
              >
                {showWelcome && (
                  <div className="flex min-h-[40%] flex-col justify-center gap-6 py-4">
                    <div>
                      <p className="text-[11px] font-mono uppercase tracking-[0.16em] text-vx-accent">
                        VX Assistant
                      </p>
                      <h2 className="mt-3 text-2xl font-semibold leading-snug tracking-tight text-vx-ink small:text-[28px]">
                        {t(locale, "chat.greeting")}
                      </h2>
                      <p className="mt-3 max-w-sm text-sm leading-6 text-vx-muted">
                        {t(locale, "chat.greetingHint")}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2">
                      {suggestedPrompts.map((prompt) => (
                        <button
                          key={prompt}
                          type="button"
                          disabled={loading}
                          onClick={() => void sendText(prompt)}
                          className="rounded-full border border-vx-line bg-vx-bg px-4 py-2.5 text-left text-sm leading-5 text-vx-ink transition-colors hover:border-vx-accent hover:text-vx-accent disabled:opacity-50"
                          data-testid="chat-suggested-prompt"
                        >
                          {prompt}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={[
                      "max-w-[95%] rounded-2xl px-4 py-3 text-[15px] leading-7",
                      message.role === "user"
                        ? "ml-auto bg-vx-accent text-white"
                        : "mr-auto border border-vx-line bg-vx-bg text-vx-ink",
                    ].join(" ")}
                  >
                    <p className="whitespace-pre-wrap">{message.content}</p>
                    {!!message.products?.length && (
                      <ul className="mt-3 space-y-2">
                        {message.products.map((product) => (
                          <li key={product.id}>
                            <LocalizedClientLink
                              href={`/products/${product.handle}`}
                              className="flex gap-3 rounded-xl border border-vx-line bg-vx-elevated p-2.5 transition-colors hover:border-vx-accent"
                              onClick={() => setOpen(false)}
                            >
                              <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-vx-bg">
                                {product.thumbnail ? (
                                  // eslint-disable-next-line @next/next/no-img-element
                                  <img
                                    src={product.thumbnail}
                                    alt=""
                                    className="h-full w-full object-contain"
                                  />
                                ) : null}
                              </div>
                              <div className="min-w-0 self-center">
                                <p className="line-clamp-2 text-[13px] font-medium text-vx-ink">
                                  {product.title}
                                </p>
                                {product.price && (
                                  <p className="mt-1 text-[13px] text-vx-accent">
                                    {product.price}
                                  </p>
                                )}
                              </div>
                            </LocalizedClientLink>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}

                {loading && (
                  <div
                    className="mr-auto rounded-2xl border border-vx-line bg-vx-bg px-4 py-3"
                    data-testid="chat-loading-bubble"
                  >
                    <SearchingWorm />
                  </div>
                )}
              </div>

              <form
                onSubmit={sendMessage}
                className="shrink-0 border-t border-vx-line bg-vx-elevated p-4"
              >
                <div className="flex items-end gap-2 rounded-2xl border border-vx-line bg-vx-bg p-2 focus-within:border-vx-accent">
                  <label className="sr-only" htmlFor="chat-widget-input">
                    ข้อความ
                  </label>
                  <textarea
                    id="chat-widget-input"
                    rows={2}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault()
                        void sendMessage()
                      }
                    }}
                    placeholder={t(locale, "chat.placeholderAssistant")}
                    className="max-h-32 min-h-[52px] flex-1 resize-none border-0 bg-transparent px-2 py-2 text-[15px] text-vx-ink outline-none placeholder:text-vx-muted"
                    data-testid="chat-widget-input"
                  />
                  <button
                    type="submit"
                    disabled={loading || !input.trim()}
                    className="mb-0.5 shrink-0 rounded-xl bg-vx-accent px-4 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-white disabled:opacity-50"
                    data-testid="chat-widget-send"
                  >
                    ส่ง
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      )}
    </>
  )
}
